import React, { useMemo } from "react";
import ModalTableInput from "./ModalTableInput";
import "../../App.css";

const COPOMapping = ({
  courseOutcomes,
  programOutcomes,
  copoMapping,
  onCOPOMappingChange,
  onCOChange,
  onCOAdd,
  onCORemove,
  isDisabled = false,
}) => {
  // Generate headers dynamically based on program outcomes
  const generateHeaders = () => {
    const headers = [
      {
        key: "code",
        label: "CO Code",
        width: "1fr",
        required: true,
        placeholder: "e.g., CO1",
      },
      {
        key: "description",
        label: "CO Description",
        width: "2.5fr",
        required: true,
        placeholder: "Enter course outcome description",
      },
    ];

    // Add PO columns
    programOutcomes?.forEach((po) => {
      headers.push({
        key: `PO_${po.code}`,
        label: po.code,
        width: "0.6fr",
        type: "number",
        min: 1,
        max: 3,
      });
    });

    headers.push({
      key: "actions",
      label: "Actions",
      width: "0.8fr",
    });

    return headers;
  };

  // Transform course outcomes to include PO mappings with unique identifiers
  // Use useMemo to ensure unique IDs are stable across re-renders
  const transformedRows = useMemo(() => {
    return courseOutcomes.map((co, index) => {
      const row = {
        // Generate a truly unique ID that's stable for this specific row
        _uniqueId:
          co._id ||
          (co.code
            ? `co_${co.code}_${index}`
            : `co_temp_${index}_${Math.random()}`),
        code: co.code,
        description: co.description,
        _rowIndex: index, // Store row index for reference
      };

      // Add PO mapping values - look up by BOTH code and index
      programOutcomes?.forEach((po) => {
        // First try to find by CO code
        let mapping = copoMapping?.find(
          (m) => m.COCode === co.code && m.POCode === po.code
        );

        // If not found and code is empty/temp, try to find by row index
        // This preserves weights when CO code changes
        if (!mapping && copoMapping) {
          // Look for mappings that might belong to this row position
          const tempCOCode = `_temp_${index}`;
          mapping = copoMapping.find(
            (m) => m.COCode === tempCOCode && m.POCode === po.code
          );
        }

        row[`PO_${po.code}`] = mapping?.weight || "";
      });

      return row;
    });
  }, [courseOutcomes, programOutcomes, copoMapping]);

  const handleCellChange = (rowIndex, field, value) => {
    if (field === "code" || field === "description") {
      // Update course outcome
      onCOChange(rowIndex, field, value);
    } else if (field.startsWith("PO_")) {
      // Update COPO mapping
      const poCode = field.replace("PO_", "");
      let coCode = courseOutcomes[rowIndex].code;

      // If CO code is empty, use temporary identifier based on row index
      if (!coCode || coCode.trim() === "") {
        coCode = `_temp_${rowIndex}`;
      }

      // Handle empty value - always allow clearing
      if (value === "" || value === null || value === undefined) {
        onCOPOMappingChange(coCode, poCode, 0);
        return;
      }

      // Parse value
      const numValue = parseInt(value, 10);

      // Save mapping with either real CO code or temporary identifier
      if (!isNaN(numValue) && numValue >= 1 && numValue <= 3) {
        onCOPOMappingChange(coCode, poCode, numValue);
      }
    }
  };

  const handleCOCodeBlur = (rowIndex, value) => {
    // Only migrate temporary mappings when user finishes editing (onBlur)
    if (value && value.trim() !== "") {
      const oldTempCode = `_temp_${rowIndex}`;
      const newCOCode = value.trim();

      // Check if there are any temporary mappings to migrate
      const hasTempMappings = copoMapping?.some(
        (m) => m.COCode === oldTempCode && m.weight > 0
      );

      if (hasTempMappings) {
        // Find all mappings with the temporary code and update them
        programOutcomes?.forEach((po) => {
          const tempMapping = copoMapping?.find(
            (m) => m.COCode === oldTempCode && m.POCode === po.code
          );

          if (tempMapping && tempMapping.weight > 0) {
            // Remove the old temporary mapping and create new one with actual CO code
            onCOPOMappingChange(oldTempCode, po.code, 0); // Clear temp
            onCOPOMappingChange(newCOCode, po.code, tempMapping.weight); // Set with real code
          }
        });
      }
    }
  };

  const headers = generateHeaders();

  return (
    <div className="copo-mapping-container">
      <div className="copo-header">
        <h3>Course Outcomes & CO-PO Mapping</h3>
        <p className="helper-text">
          Define course outcomes and map them to program outcomes. Enter values
          1-3 for mapping strength (1=Low, 2=Medium, 3=High). Leave blank for no
          mapping.
        </p>
      </div>

      <ModalTableInput
        headers={headers}
        rows={transformedRows}
        onRowChange={handleCellChange}
        onRowRemove={onCORemove}
        onRowAdd={onCOAdd}
        canRemove={true}
        addButtonText="Add Course Outcome"
        rowKeyField="_uniqueId"
        renderCell={(header, row, rowIndex) => {
          switch (header.key) {
            case "code":
              return (
                <input
                  type="text"
                  value={row.code || ""}
                  placeholder={header.placeholder}
                  onChange={(e) =>
                    handleCellChange(rowIndex, "code", e.target.value)
                  }
                  onBlur={(e) => handleCOCodeBlur(rowIndex, e.target.value)}
                  disabled={isDisabled}
                  required
                  className="modal-table-input"
                />
              );

            case "description":
              return (
                <input
                  type="text"
                  value={row.description || ""}
                  placeholder={header.placeholder}
                  onChange={(e) =>
                    handleCellChange(rowIndex, "description", e.target.value)
                  }
                  disabled={isDisabled}
                  required
                  className="modal-table-input"
                />
              );

            case "actions":
              return (
                <button
                  type="button"
                  onClick={() => onCORemove(rowIndex)}
                  disabled={courseOutcomes.length <= 1 || isDisabled}
                  className="modal-table-remove-btn"
                  title={
                    courseOutcomes.length <= 1
                      ? "At least one outcome is required"
                      : "Remove this outcome"
                  }
                >
                  <i className="fa-solid fa-trash"></i>
                </button>
              );

            default:
              // PO columns
              if (header.key.startsWith("PO_")) {
                const inputValue = row[header.key] || "";

                return (
                  <input
                    key={`${row._uniqueId}-${header.key}`}
                    type="text"
                    inputMode="numeric"
                    pattern="[1-3]"
                    value={inputValue}
                    placeholder="-"
                    onChange={(e) => {
                      const val = e.target.value;
                      // Allow only digits 1-3 or empty
                      if (val === "" || /^[1-3]$/.test(val)) {
                        handleCellChange(rowIndex, header.key, val);
                      }
                    }}
                    onBlur={(e) => {
                      const val = e.target.value;
                      // Clear if invalid
                      if (val !== "" && (isNaN(val) || val < 1 || val > 3)) {
                        handleCellChange(rowIndex, header.key, "");
                      }
                    }}
                    disabled={isDisabled}
                    className="modal-table-input copo-mapping-input"
                    maxLength="1"
                  />
                );
              }
              return null;
          }
        }}
      />
    </div>
  );
};

export default COPOMapping;
